import { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { List, ListItem, ListItemText, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { jwt } from "../utils/index.js";

const useStyles = makeStyles((theme) => ({
	sidebar: {
		height: "100%",
		position: "absolute",
		color: theme.palette.third,
		overflow: "auto"
	},
	listItem: {
		marginBottom: theme.spacing(1),
		padding: theme.spacing(0.2, 1),
		width: "220px",
		minHeight: "45px",
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.third,
		borderRadius: '12px',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: theme.palette.secondary.main,
			color: '#000',
		},
		'& .MuiListItemText-primary': {
			color: 'white !important',
		},
		'&:visited .MuiListItemText-primary': {
			color: 'white !important',
		},
		'&:link .MuiListItemText-primary': {
			color: 'white !important',
		},
	},
	listItemSelected: {
		backgroundColor: `${theme.palette.primary.main}!important`,
		color: '#000',
	},
	nested: {
		paddingLeft: theme.spacing(4),
		textDecoration: 'none',
		'& .MuiListItemText-primary': {
			color: 'black !important',
		},
		'&:visited .MuiListItemText-primary': {
			color: 'black !important',
		},
		'&:link .MuiListItemText-primary': {
			color: 'black !important',
		},
	},
}));

// Paths a KentroUser is allowed to see — extend this array to grant access to more screens
const KENTRO_USER_ALLOWED_PATHS = [
	"/info/animal/registration",
	"/info/owner/population",
	"/info/male/tree",
	"/info/animal/relatives",
	"/info/owner/population/detailed",
];

const ButtonWithText = ({ text, more, handler, userOrg, isKentroUser }) => {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();

	const handleClick = () => {
		setOpen(!open);
		if (handler) handler();
	};

	const shouldShowMenuItem = (item) => {
		// KentroUser: only show sub-items whose path is in the allowlist
		if (isKentroUser) {
			const path = item.handler.toString().match(/"([^"]+)"/)?.[1];
			return KENTRO_USER_ALLOWED_PATHS.includes(path);
		}
		if (item.notOrg && userOrg && item.notOrg.includes(userOrg)) {
			return false;
		}
		return true;
	};

	const handleMenuItemClick = (e, path) => {
		if (e.button === 1 || (e.button === 0 && (e.ctrlKey || e.metaKey))) {
			return;
		}
		e.preventDefault();
		if (location.pathname === path) {
			window.location.reload();
		} else {
			navigate(path);
		}
	};

	const filteredMoreItems = more ? more.filter(shouldShowMenuItem) : [];

	return (
		<div>
			<ListItem button onClick={handleClick} className={classes.listItem}>
				<ListItemText primary={text} sx={{ textTransform: "capitalize", color: "white.main" }} />
				{more ? (open ? <ExpandLess sx={{ color: "white.main" }} /> : <ExpandMore sx={{ color: "white.main" }} />) : null}
			</ListItem>
			{more && (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{filteredMoreItems.map((el) => {
							const path = el.handler.toString().match(/"([^"]+)"/)?.[1] || "/";
							return (
								<ListItem 
									button 
									key={el.title} 
									className={classes.nested}
									component="a"
									href={path}
									onMouseDown={(e) => handleMenuItemClick(e, path)}
									onClick={(e) => e.preventDefault()}
								>
									<ListItemText primary={el.title} sx={{ color: "black.main" }} />
								</ListItem>
							);
						})}
					</List>
				</Collapse>
			)}
		</div>
	);
};

const ButtonSimple = ({ text, handler }) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const location = useLocation();

	const path = handler.toString().match(/"([^"]+)"/)?.[1] || "/";

	const handleMenuItemClick = (e) => {
		if (e.button === 1 || (e.button === 0 && (e.ctrlKey || e.metaKey))) {
			// Middle click or Ctrl/Cmd+Click - let browser handle it
			return;
		}
		e.preventDefault();
		if (location.pathname === path) {
			window.location.reload();
		} else {
			navigate(path);
		}
	};

	return (
		<ListItem 
			button 
			className={classes.listItem}
			component="a"
			href={path}
			onMouseDown={handleMenuItemClick}
			onClick={(e) => e.preventDefault()}
		>
			<ListItemText primary={text} sx={{ textTransform: "capitalize", color: "white.main" }} />
		</ListItem>
	);
};

const Sidebar = ({ isSmall: sidebarIsSmall }) => {
	const [isSmall, setIsSmall] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const classes = useStyles();
	const [isAdmin, setIsAdmin] = useState(false);
	const [isKentroUser, setIsKentroUser] = useState(false);
	const [userOrg, setUserOrg] = useState(null);

	useEffect(() => {
		const checkAdminStatus = async () => {
			const adminStatus = await jwt.isAdmin();
			setIsAdmin(adminStatus);
		};
		checkAdminStatus();

		const checkKentroUserStatus = async () => {
			const kentroStatus = await jwt.isKentroUser();
			setIsKentroUser(kentroStatus);
		};
		checkKentroUserStatus();

		const fetchUserOrg = async () => {
			const org = await jwt.getUserOrg();
			setUserOrg(org);
		}
		fetchUserOrg();
	}, []);

	useEffect(() => setIsSmall(sidebarIsSmall), [sidebarIsSmall]);

	const shouldShowButton = (button) => {
		// KentroUser: only show buttons/groups that contain at least one allowed path
		if (isKentroUser) {
			if (button.more) {
				return button.more.some((item) => {
					const path = item.handler.toString().match(/"([^"]+)"/)?.[1];
					return KENTRO_USER_ALLOWED_PATHS.includes(path);
				});
			}
			const path = button.handler?.toString().match(/"([^"]+)"/)?.[1];
			return KENTRO_USER_ALLOWED_PATHS.includes(path);
		}
		if (button.notOrg && userOrg && button.notOrg.includes(userOrg)) {
			return false;
		}
		return true;
	};

	const handleNavigation = (path) => {
		if (location.pathname === path) {
			window.location.reload();
		} else {
			navigate(path);
		}
	};

	const buttons = [
		{
			text: "Εκτροφές",
			more: [
				{ title: "Εισαγωγή Νέας Εκτροφής", handler: () => handleNavigation("/owners/create") },
				{ title: "Στοιχεία εκτροφής", handler: () => handleNavigation("/owners/select") },
				{ title: "Αλλαγή Κωδικού Εκτροφής", handler: () => handleNavigation("/owners/change-code") },
				{ title: "Λίστα Εκτροφών", handler: () => handleNavigation("/owners/preview") },
				{ title: "Αρχειοθέτηση Εκτροφών", handler: () => handleNavigation("/owners/archive") },
			],
			admin: false
		},
		{
			text: "Ελεγκτές",
			more: [
				{ title: "Εισαγωγή Νέου Ελεγκτή", handler: () => handleNavigation("/inspectors/create") },
				{ title: "Στοιχεία Ελεγκτή", handler: () => handleNavigation("/inspectors/select") },
				{ title: "Λίστα Ελεγκτών", handler: () => handleNavigation("/inspectors/preview") },
			],
			admin: false
		},
		{
			text: "Ζώα",
			more: [
				{ title: "Εισαγωγή Νέου Ζώου", handler: () => handleNavigation("/animals/create") },
				{ title: "Επίβλεψη Ζώων", handler: () => handleNavigation("/animals/supervision") },
				{ title: "Στοιχεία Ζώου", handler: () => handleNavigation("/animals/select") },
				{ title: "Λίστα Ζώων", handler: () => handleNavigation("/animals/preview") },
				{ title: "Εισαγωγή Στοιχείων Μορφολογίας Μαστού", handler: () => handleNavigation("/animals/udder/create") },
				{ title: "Προβολή / Ενημέρωση Μορφολογίας Μαστού", handler: () => handleNavigation("/animals/udder/select") },
				{ title: "Αλλαγή Κωδικού Γενεαλογικού Βιβλίου", handler: () => handleNavigation("/animals/registration-change") },
				{ title: "Αναπαραγωγικό Ιστορικό Εκτροφής", handler: () => handleNavigation("/animals/reproductive-history") },
				{ title: "Τρέχουσα Εκτροφή Ζώου", handler: () => handleNavigation("/animals/current-owner") },
				{ title: "Ομαδική Απομάκρυνση Ζώων", handler: () => handleNavigation("/animals/group-culling") },
			],
			admin: false
		},
		{
			text: "Συζεύξεις",
			more: [
				{ title: "Εισαγωγή Σύζευξης", handler: () => handleNavigation("/matings/create") },
				{ title: "Λίστα Συζεύξεων", handler: () => handleNavigation("/matings/preview") },
				// { title: "Λίστα Συζεύξεων Κριών", handler: () => handleNavigation("/matings/preview?aries") },
				{ title: "Έλεγχος Συγγένειας", handler: () => handleNavigation("/matings/relationships") },
				{ title: "Διαγραφή Σύζευξης", handler: () => handleNavigation("/matings/delete") },
			],
			admin: false
		},
		{
			text: "Τοκετοί",
			more: [
				{ title: "Εισαγωγή Νέου Τοκετού", handler: () => handleNavigation("/lambings/create") },
				{ title: "Διαχείριση Τοκετών", handler: () => handleNavigation("/lambings/select") },
				{ title: "Διαχείριση Αμνών", handler: () => handleNavigation("/lambings/manage") },
				{ title: "Ιστορικό Κωδικού Αμνού", handler: () => handleNavigation("/lambings/lamb-code-history") },
				{ title: "Εισαγωγή Στοιχείων Βάρους Αμνού", handler: () => handleNavigation("/lambings/lamb-weight") },
				{ title: "Λήξη Περιόδου Αμνών", handler: () => handleNavigation("/lambings/lambs/end") },
				{ title: "Διαγραφή Τοκετού", handler: () => handleNavigation("/lambings/delete") },
			],
			admin: false
		},
		{
			text: "Γαλακτοπαραγωγή",
			more: [
				{ title: "Εισαγωγή Στοιχείων Ελέγχου", handler: () => handleNavigation("/milk-recordings/create") },
				{ title: "Στοιχεία Ελέγχου", handler: () => handleNavigation("/milk-recordings/select") },
				{ title: "Εισαγωγή Μετρήσεων Γαλακτοπαραγωγής", handler: () => handleNavigation("/animals-milk-recordings/create") },
				{ title: "Διόρθωση Μετρήσεων Γαλακτοπαραγωγής", handler: () => handleNavigation("/animals-milk-recordings/select") },
				{ title: "Λίπος, Πρωτεΐνη, Λακτόζη", handler: () => handleNavigation("/animals-milk-recordings/metrics") },
				{ title: "Υπολογισμοί Συνολικής Γαλακτοπαραγωγής", handler: () => handleNavigation("/animals-milk-recordings/calculation") },
				{ title: "Λήξη Γαλακτοπαραγωγής", handler: () => handleNavigation("/animals-milk-recordings/end") },
				{ title: "Διόρθωση Λήξης Γαλακτοπαραγωγής", handler: () => handleNavigation("/animals-milk-recordings/end/update") },
			],
			admin: false,
			notOrg: ["2"],
		},
		{
			text: "Πώληση Ζώων",
			more: [
				{ title: "Εισαγωγή Παραγγελίας", handler: () => handleNavigation("/sales/orders/create") },
				{ title: "Ιστορικό Παραγγελιών", handler: () => handleNavigation("/sales/orders") },
				{ title: "Διαθεσιμότητα Ζώων", handler: () => handleNavigation("/sales/animals") },
				{ title: "Δήλωση Ζώων Προς Πώληση", handler: () => handleNavigation("/sales/animals/create") },
				{ title: "Εμφάνιση/Διόρθωση Ζώων Προς Πώληση", handler: () => handleNavigation("/sales/animals/select") },
				{ title: "Μεταφορά Ζώου", handler: () => handleNavigation("/sales/animals/transfer") },
				{ title: "Μεταβίβαση Εκτροφής", handler: () => handleNavigation("/sales/owners/transfer") },
			],
			admin: false
		},
		{
			text: "Πληροφορίες",
			more: [
				// { title: "Γενεολογικό Δέντρο", handler: () => handleNavigation("/info/pedigree") },
				{ title: "Γενεολογικό Δέντρο Κριού", handler: () => handleNavigation("/info/male/tree") },
				{ title: "Συγγενείς Ζώου", handler: () => handleNavigation("/info/animal/relatives") },
				{ title: "Γενεαλογικό Πιστοποιητικό Ζώου", handler: () => handleNavigation("/info/animal/registration") },
				{ title: "Μηνιαία Γαλακτοπαραγωγή", handler: () => handleNavigation("/info/monthly/milk-production"), notOrg: ["2"] },
				{ title: "Στοιχεία Πληθυσμού", handler: () => handleNavigation("/info/owner/population/detailed") },
				{ title: "Καρτέλα Απογραφής", handler: () => handleNavigation("/info/owner/population") },
				{ title: "Ιστορικό Γαλακτοπαραγωγής Ζώων", handler: () => handleNavigation("/info/year-milking"), notOrg: ["2"] },
				{ title: "Καταγραφή Προβλημάτων", handler: () => handleNavigation("/info/problems/report") },
			],
			admin: false
		},
		{
			text: "SCRAPIE",
			more: [
				{ title: "Εισαγωγή Στοιχείων", handler: () => handleNavigation("/scrapie/add") },
				{ title: "Διόρθωση Στοιχείων", handler: () => handleNavigation("/scrapie/update") },
				{ title: "Διαγραφή Στοιχείων", handler: () => handleNavigation("/scrapie/delete") },
				{ title: "Εμφάνιση Στοιχείων", handler: () => handleNavigation("/scrapie/view") },
				{ title: "Εισαγωγή Στοιχείων Αμνών", handler: () => handleNavigation("/scrapie/lamb-add") },
				{ title: "Διόρθωση Στοιχείων Αμνών", handler: () => handleNavigation("/scrapie/lamb-update") },
				{ title: "Διαγραφή Στοιχείων Αμνών", handler: () => handleNavigation("/scrapie/lamb-delete") },
				{ title: "Εμφάνιση Στοιχείων Αμνών", handler: () => handleNavigation("/scrapie/lamb-view") },
			],
			admin: false
		},

		{
			text: "Στοιχεία Συνεταιρισμού",
			handler: () => handleNavigation("/organization"),
			admin: false
		},
		{
			text: "Χρήστες",
			handler: () => handleNavigation("/users"),
			admin: true
		},
		{
			text: "Προσκεκλημένοι Χρήστες",
			handler: () => handleNavigation("/invited-users"),
			admin: true
		},
		{
			text: "Πρόσκληση νέου χρήστη",
			handler: () => handleNavigation("/create-invited-user"),
			admin: true
		},
		{
			text: "Ενέργειες Διαχειριστή",
			handler: () => handleNavigation("/admin-actions"),
			admin: true
		},
	];

	return (
		<div className={classes.sidebar} style={{ width: isSmall ? "50px" : "260px", padding: "20px 5px", textAlign: "center" }}>
			<List>
				{buttons
					.filter(shouldShowButton)
					.map((button) => button.more ? (
						!button.admin ? (
							<ButtonWithText
								key={button.text}
								icon={button.icon}
								text={button.text}
								more={button.more}
								userOrg={userOrg}
								isKentroUser={isKentroUser}
							/>) : (isAdmin ? (
								<ButtonWithText
									key={button.text}
									icon={button.icon}
									text={button.text}
									more={button.more}
									userOrg={userOrg}
									isKentroUser={isKentroUser}
								/>
							) : (
								<></>
							)
						)
					) : (!button.admin ? (
						<ButtonSimple
							key={button.text}
							icon={button.icon}
							text={button.text}
							handler={button.handler}
						/>) : (isAdmin ? (
							<ButtonSimple
								key={button.text}
								icon={button.icon}
								text={button.text}
								handler={button.handler}
							/>
						) : (<></>)
					)
					))}
			</List>
		</div >
	);
};

export default Sidebar;
